// ── 30 napos likviditás-előrejelzés ────────────────────────────────────────
//
// Heti be-/kifizetés-oszlopok + kumulált egyenleg-vonal, KÉT PANELBEN, közös
// X-tengelyen. (Gábor döntése, 2026-08-06 — a Float/Agicap 13 hetes gördülő
// minta 30 napra méretezve.)
//
// ⚠⚠ MIÉRT KÉT PANEL, ÉS NEM EGY (2026-08-06, Gábor mérése élő adaton)
//
// Az első változat egyetlen tengelyre tette az oszlopokat és a vonalat. Mérve:
// az egyenleg 1 010 milliárd Ft (Raiffeisen sandbox), a heti forgalom ~10 millió
// — a vonal így az egész tengelyt elvitte, az oszlopok pedig **láthatatlan
// csíkká lapultak az alapvonalon**. Ez NEM sandbox-jelenség: bármelyik cégnél
// előjön, ahol a készpénzállomány nagyságrenddel nagyobb a heti forgalomnál.
//
// A kézenfekvő javítás — másodlagos tengely — TILOS: két különböző skála egy
// charton azt jelenti, hogy a két sorozat metszéspontja a skálák önkényes
// választásától függ, nem az adattól. Ez az adatvizualizáció leggyakoribb hibája.
//
// A helyes megoldás a két, egymás alatti panel közös kategória-tengelyen:
//   · felül  az egyenleg-VONAL — saját skála, nem nulla-alapú;
//   · alul   a be-/kifizetés-OSZLOPOK — mindig nulla-alapú.
// A szabály, ami ezt megengedi: az oszlop HOSSZAL kódol, ezért a nullától kell
// indulnia; a vonal POZÍCIÓVAL kódol, ezért nem. A `syncId` köti össze a kettőt:
// egy hét fölé állva mindkét panel ugyanazt a kategóriát emeli ki.
//
// ⚠ A nyitó egyenleg KÜLÖN érkezik (`/balances`, lassú banki hívás), ezért a
// `startBalance` lehet null. Olyankor csak az oszlop-panel jelenik meg — és ezt
// ki is írjuk. Egy 0-ról induló vonal azt állítaná, hogy nincs pénz a számlákon;
// ugyanaz a hiba, amit a BalancesCard hiba-ága kerül.
import {
  ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis,
  CartesianGrid, Tooltip, Legend, ReferenceDot, ReferenceLine,
} from 'recharts';
import { huFmt, huAxis } from '../../utils';
import { axisTick, gridProps, legendProps, barCursor } from './ChartFrame';
import { useEnterAnimation } from '../../hooks/useMotion';

const SERIES = {
  in:      { key: 'in_huf',          name: 'Várható bevétel', color: 'var(--chart-in)' },
  out:     { key: 'out_current_huf', name: 'Fizetendő',       color: 'var(--chart-out)' },
  overdue: { key: 'overdue_out_huf', name: 'Ebből lejárt',    color: 'var(--danger)' },
};

// A két panel plot-területe CSAK akkor esik egybe, ha a bal margó és a
// tengely-szélesség azonos. Ezért konstans, nem panelenként megadott érték.
const AXIS_W = 62;
const MARGIN = { top: 16, right: 14, left: 0, bottom: 0 };

function Tip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const r = payload[0].payload;
  const row = (label, value, color, muted) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
      {color
        ? <span style={{ width: 8, height: 8, borderRadius: 2, flexShrink: 0, background: color }} />
        : <span style={{ width: 8, flexShrink: 0 }} />}
      <span style={{ color: muted ? 'var(--text-muted)' : 'var(--text)' }}>{label}</span>
      <span style={{ marginLeft: 'auto', fontFamily: 'var(--mono)', paddingLeft: 12,
        fontVariantNumeric: 'tabular-nums',
        color: muted ? 'var(--text-muted)' : 'var(--text)' }}>{value}</span>
    </div>
  );
  return (
    <div style={{ background: 'var(--bg-surface)', border: '1px solid var(--border)',
      borderRadius: 'var(--radius)', padding: '0.55rem 0.75rem', boxShadow: 'var(--shadow)',
      fontSize: 'var(--fs-xs)', minWidth: 200 }}>
      <div style={{ color: 'var(--text-muted)', fontWeight: 600, marginBottom: 6 }}>
        {r.isToday ? 'Ma' : r.label}
      </div>
      {!r.isToday && (
        <>
          {row('Várható bevétel', `+${huFmt(r.in_huf)} Ft`, SERIES.in.color)}
          {row('Fizetendő', `−${huFmt(r.out_huf)} Ft`, SERIES.out.color)}
          {r.overdue_out_huf > 0 &&
            row(`ebből lejárt (${r.overdue_out_count} db)`,
              `−${huFmt(r.overdue_out_huf)} Ft`, SERIES.overdue.color, true)}
        </>
      )}
      {r.balance != null && (
        <div style={{ borderTop: '1px solid var(--border-dim)', marginTop: 5, paddingTop: 5 }}>
          {row(r.isToday ? 'Egyenleg most' : 'Egyenleg a hét végén',
            `${huFmt(r.balance)} Ft`, null)}
        </div>
      )}
    </div>
  );
}

const PanelLabel = ({ children, note }) => (
  <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 2 }}>
    <span style={{ fontSize: 'var(--fs-xs)', fontWeight: 600, letterSpacing: '0.06em',
      textTransform: 'uppercase', color: 'var(--text-muted)' }}>{children}</span>
    {note && <span style={{ fontSize: 'var(--fs-xs)', color: 'var(--text-dim)' }}>{note}</span>}
  </div>
);

export default function ForecastChart({
  buckets = [], startBalance = null, balancePending = false,
  balanceHeight = 152, flowHeight = 150,
}) {
  const animate = useEnterAnimation(700);

  // A vonal horgonya: a „Ma” oszlop-nélküli kategória. Enélkül a vonal az első
  // hét VÉGÉN kezdődne, és nem látszana a kiindulási egyenleg.
  const rows = [{ label: 'Ma', isToday: true, in_huf: 0, out_huf: 0,
    out_current_huf: 0, overdue_out_huf: 0, overdue_out_count: 0,
    balance: startBalance }];

  let running = startBalance;
  buckets.forEach((b) => {
    if (running != null) running = running + (b.in_huf || 0) - (b.out_huf || 0);
    rows.push({
      ...b,
      // A lejárt rész KÜLÖN szegmensként ül a fizetendő oszlopon: így egy
      // pillantással látszik, mennyi a „most azonnal” és mennyi a „még ráér”.
      out_current_huf: Math.max((b.out_huf || 0) - (b.overdue_out_huf || 0), 0),
      balance: running,
    });
  });

  const hasBalance = startBalance != null;
  const bals = rows.filter((r) => r.balance != null).map((r) => r.balance);
  const low = hasBalance
    ? rows.filter((r) => r.balance != null)
        .reduce((a, b) => (b.balance < a.balance ? b : a))
    : null;
  const showLow = low && bals.length > 1 && !low.isToday;
  const goesNegative = bals.some((v) => v < 0);

  // A vonal-panel skálája: szűk sáv az adat köré, hogy a 30 napos MOZGÁS
  // látszódjon. A 0-t akkor és csak akkor engedjük be, ha az adat átlépi —
  // különben egy nagy egyenlegnél az egész panel egyetlen lapos vonal lenne.
  const bMin = bals.length ? Math.min(...bals) : 0;
  const bMax = bals.length ? Math.max(...bals) : 0;
  const bPad = Math.max((bMax - bMin) * 0.22, Math.abs(bMax) * 0.01, 1);
  const bDomain = [
    goesNegative ? Math.min(bMin - bPad, 0) : bMin - bPad,
    bMax + bPad,
  ];

  const sharedX = (hide) => (
    <XAxis dataKey="label" tick={hide ? false : axisTick} interval={0}
      tickLine={false} height={hide ? 1 : 30}
      axisLine={hide ? false : { stroke: 'var(--border)' }} />
  );

  return (
    <>
      {/* ⚠ Amíg a bankegyenlegek töltenek, a felső panel HELYÉT lefoglaljuk.
          Enélkül a kártya ~180 px-t ugrana a banki válasz megérkezésekor — és a
          `/balances` mérve 13 mp-ig is elhúzódhat, tehát ezt tényleg látná a
          felhasználó. A csontváz a végleges elrendezést mutatja, nem pörgőt. */}
      {!hasBalance && balancePending && (
        <>
          <PanelLabel>Várható egyenleg</PanelLabel>
          <div style={{ height: balanceHeight, borderRadius: 'var(--radius)',
            border: '1px dashed var(--border)', display: 'flex',
            alignItems: 'center', justifyContent: 'center' }}>
            <span style={{ fontSize: 'var(--fs-xs)', color: 'var(--text-muted)' }}>
              Egyenlegek lekérdezése a bankoktól…
            </span>
          </div>
        </>
      )}

      {hasBalance && (
        <>
          <PanelLabel note="a 30 nap mozgása — a skála nem nulláról indul">
            Várható egyenleg
          </PanelLabel>
          <ResponsiveContainer width="100%" height={balanceHeight}>
            <ComposedChart data={rows} margin={MARGIN} syncId="pf-forecast">
              <CartesianGrid {...gridProps} />
              {sharedX(true)}
              <YAxis tickFormatter={huAxis} tick={axisTick} axisLine={false}
                tickLine={false} width={AXIS_W} domain={bDomain} />
              <Tooltip cursor={barCursor} content={<Tip />} />
              {/* A 0-vonal CSAK akkor, ha a projekció tényleg alá megy. */}
              {goesNegative && (
                <ReferenceLine y={0} stroke="var(--danger)" strokeWidth={1.5}
                  strokeDasharray="4 3" />
              )}
              <Line type="linear" dataKey="balance" name="Várható egyenleg"
                stroke="var(--text)" strokeWidth={2.5} isAnimationActive={animate}
                dot={{ r: 3.5, fill: 'var(--text)', stroke: 'var(--bg-card)', strokeWidth: 2 }}
                activeDot={{ r: 5 }} />
              {showLow && (
                <ReferenceDot x={low.label} y={low.balance} r={6} isFront
                  fill="none" stroke="var(--warning)" strokeWidth={2}
                  label={{ value: `min ${huAxis(low.balance)}`, position: 'top',
                    fill: 'var(--warning)', fontSize: 11, fontFamily: 'var(--mono)',
                    fontWeight: 600, offset: 10 }} />
              )}
            </ComposedChart>
          </ResponsiveContainer>
        </>
      )}

      <PanelLabel note={hasBalance ? 'ugyanazok a hetek, nulla-alapú skála' : undefined}>
        Be- és kifizetés
      </PanelLabel>
      <ResponsiveContainer width="100%" height={flowHeight}>
        <ComposedChart data={rows} barGap={2} barCategoryGap="24%"
          margin={MARGIN} syncId="pf-forecast">
          <CartesianGrid {...gridProps} />
          {sharedX(false)}
          <YAxis tickFormatter={huAxis} tick={axisTick} axisLine={false}
            tickLine={false} width={AXIS_W} />
          <Tooltip cursor={barCursor} content={<Tip />} />
          <Legend {...legendProps} />
          <Bar dataKey={SERIES.in.key} name={SERIES.in.name} fill={SERIES.in.color}
            stackId="in" radius={[4, 4, 0, 0]} maxBarSize={26} isAnimationActive={animate} />
          <Bar dataKey={SERIES.out.key} name={SERIES.out.name} fill={SERIES.out.color}
            stackId="out" maxBarSize={26} isAnimationActive={animate} />
          <Bar dataKey={SERIES.overdue.key} name={SERIES.overdue.name}
            fill={SERIES.overdue.color} stackId="out" radius={[4, 4, 0, 0]}
            maxBarSize={26} isAnimationActive={animate} />
        </ComposedChart>
      </ResponsiveContainer>

      {!hasBalance && !balancePending && (
        <p style={{ fontSize: 'var(--fs-xs)', color: 'var(--warning)', marginTop: 6 }}>
          Az egyenleg-panel hiányzik: a bankegyenlegek nem elérhetők.
          Az oszlopok érvényesek.
        </p>
      )}
    </>
  );
}
